import isPropValid from '@emotion/is-prop-valid'
import { css, styled } from '@mui/material/styles'
import { PaletteColor } from '@pbt/pbt-ui-components'
import { getThemeColor } from '@pbt/pbt-ui-components/src/muiTheme'

type TypographyProps = {
  color?: PaletteColor
  ellipsis?: boolean
  /** gutterBottom - spacing between blocks = `paragraph` prop in Figma */
  gutterBottom?: boolean
}

const shouldForwardProp = (prop: any) =>
  isPropValid(prop) && prop !== 'ellipsis' && prop !== 'bold'

const getCommonStyles = ({ ellipsis }: TypographyProps) => css`
  font-family: Rubik, sans-serif;
  margin: 0;
  ${ellipsis === true
    ? css`
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      `
    : ''}
`
const getColorStyle = (
  color: TypographyProps['color'],
  defaultColor: PaletteColor,
) => css`
  color: ${color ? getThemeColor(color) : getThemeColor(defaultColor)};
`

const getGutterBottomStyle = (
  { gutterBottom }: TypographyProps,
  marginBottom: string,
) =>
  gutterBottom === true
    ? css`
        margin-bottom: ${marginBottom};
      `
    : ''

export const StyledH1 = styled('h1', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  line-height: 2.6rem;
  font-size: 2rem;
  font-weight: 500;
  ${({ color }) => getColorStyle(color, 'brandPrimary')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '2.4rem')}
`

const StyledH2 = styled('h2', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  line-height: 2.6rem;
  font-size: 1.6rem;
  font-weight: 500;
  ${({ color }) => getColorStyle(color, 'brandSecondary')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '1.6rem')}
`

const StyledH3 = styled('h3', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  line-height: 2.2rem;
  font-size: 1.4rem;
  font-weight: 500;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '1.6rem')}
`

const StyledLinkLg = styled('a', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  text-decoration: underline;
  line-height: 1.9rem;
  font-size: 1.6rem;
  font-weight: 500;
  ${({ color }) => getColorStyle(color, 'brandPrimary')}
`

const StyledLinkRegular = styled('a', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  cursor: pointer;
  text-decoration: underline;
  line-height: 2.2rem;
  font-size: 1.4rem;
  font-weight: 400;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
`

const StyledLinkBold = styled('a', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  cursor: pointer;
  line-height: 2.2rem;
  font-size: 1.4rem;
  font-weight: 500;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
`

const StyledParagraphSm = styled('p', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  line-height: 1.8rem;
  font-size: 1.2rem;
  font-weight: 400;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '0.8rem')}
`

const StyledParagraphRegular = styled('p', {
  shouldForwardProp,
})<TypographyProps>`
  ${getCommonStyles};
  line-height: 2.2rem;
  font-size: 1.4rem;
  font-weight: 400;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '1.2rem')}
`

const StyledParagraphLg = styled('p', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  line-height: 2.6rem;
  font-size: 1.6rem;
  font-weight: 400;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '1.2rem')}
`

const StyledButtonLabel = styled('div', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  font-weight: 700;
  font-size: 1.4rem;
  line-height: 2rem;
  ${({ color }) => getColorStyle(color, 'grayWhite')}
`

const StyledLabel = styled('div', { shouldForwardProp })<TypographyProps>`
  ${getCommonStyles};
  font-weight: 500;
  font-size: 1.2rem;
  line-height: 2.2rem;
  ${({ color }) => getColorStyle(color, 'grayGray1')}
  ${({ gutterBottom }) => getGutterBottomStyle({ gutterBottom }, '1.2rem')}
`

export default {
  H1: StyledH1,
  H2: StyledH2,
  H3: StyledH3,
  Link: StyledLinkRegular,
  LinkBold: StyledLinkBold,
  LinkLg: StyledLinkLg,
  ParagraphSm: StyledParagraphSm,
  Paragraph: StyledParagraphRegular,
  ParagraphLg: StyledParagraphLg,
  ButtonLabel: StyledButtonLabel,
  Label: StyledLabel,
}
